import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CalculateIcon from "@mui/icons-material/Calculate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import EditNoteIcon from "@mui/icons-material/EditNote";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import { FormProvider, useForm } from "react-hook-form";
import ActionButton from "controls/global/action-button/ActionButton";
import AgencyLocationField from "pages/components/AgencyLocationField";
import { CompanyLocation } from "entities/UIModel/CompanyLocation";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { usePropertyTypeOptions } from "utils/custom-hooks/usePropertyTypeOptions";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { usePolicyTypeOptions } from "utils/custom-hooks/usePolicyTypeOptions";
import { useStateList } from "utils/custom-hooks/useStateList";
import {
  EDIT_FIELDS_BY_GROUP,
  EDIT_FIELD_BY_KEY,
  EDIT_FIELD_GROUPS,
  EditField,
} from "../product-details/editFields";

const DialogHeading = styled(DialogTitle)({
  fontSize: fontSize.xlarge,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
});

const Subtitle = styled(Typography)({
  fontSize: fontSize.xsmall,
  color: colors.grey11,
  marginTop: "-12px",
  marginBottom: gaps.small1,
  paddingLeft: padding.large1,
  paddingRight: padding.large1,
});

const RowsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 36px",
  gap: gaps.small1,
  alignItems: "start",
});

const HeaderLabel = styled(Typography)({
  fontSize: fontSize.xxsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

const AddRowButton = styled("button")({
  marginTop: gaps.small1,
  padding: `${padding.xsmall2} ${padding.medium1}`,
  background: "transparent",
  border: `1px dashed ${colors.blue01}`,
  color: colors.blue01,
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold1,
  borderRadius: borderRadius.small,
  cursor: "pointer",
  letterSpacing: "0.3px",
  display: "inline-flex",
  alignItems: "center",
  gap: gaps.xsmall2,
  "&:disabled": {
    color: colors.grey11,
    borderColor: colors.grey09,
    cursor: "not-allowed",
  },
});

const NoteSection = styled(Box)({
  marginTop: gaps.large1,
  paddingTop: gaps.medium1,
  borderTop: `1px solid ${colors.grey09}`,
});

const NoteHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: gaps.xsmall2,
});

// ---- Remit Split section (mirrors the v250 mockup's Bulk Edit) ----
const SplitsSection = styled(Box)({
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  marginBottom: gaps.medium1,
  overflow: "hidden",
});

const SplitsHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.xsmall2,
  padding: `${padding.small1} ${padding.medium1}`,
  background: colors.grey10,
  cursor: "pointer",
});

const SplitsTitle = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const SplitsBody = styled(Box)({
  padding: padding.medium1,
});

const CalcToolbar = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: gaps.small1,
  background: colors.blue20,
  border: `1px solid ${colors.blue21}`,
  borderRadius: borderRadius.small,
  padding: `${padding.small1} ${padding.medium1}`,
  marginBottom: gaps.small1,
});

const CalcLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const CalcHint = styled(Typography)({
  fontSize: fontSize.small,
  color: colors.grey11,
  flex: 1,
  minWidth: "220px",
});

const SplitsTableWrap = styled(Box)({
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  overflow: "auto",
  maxHeight: "45vh",
});

const SplitsTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: fontSize.xsmall,
});

const Th = styled("th")({
  textAlign: "left",
  padding: `${padding.xsmall2} ${padding.small1}`,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  background: colors.grey10,
});

const ThNew = styled(Th)({
  color: colors.blue15,
  background: colors.blue19,
});

const Td = styled("td")({
  padding: `${padding.xsmall2} ${padding.small1}`,
  color: colors.black,
  whiteSpace: "nowrap",
  borderTop: `1px solid ${colors.grey09}`,
});

const TdNew = styled(Td)({
  background: colors.blue19,
});

const EmptySplits = styled(Box)({
  padding: padding.medium1,
  textAlign: "center",
  fontSize: fontSize.small,
  color: colors.grey11,
});

const SplitPctInput = styled(TextField)({
  width: "90px",
  background: colors.white,
  "& .MuiInputBase-input": { textAlign: "right", padding: "4px 6px" },
});

const SplitNotice = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.xsmall2,
  minHeight: "40px",
  padding: `${padding.xsmall2} ${padding.small1}`,
  background: colors.blue20,
  border: `1px solid ${colors.blue21}`,
  borderRadius: borderRadius.small,
  color: colors.blue15,
  fontSize: fontSize.small,
});

// ---- Preview section ----
const PreviewSection = styled(Box)({
  marginTop: gaps.large1,
  border: `1px solid ${colors.blue01}`,
  borderRadius: borderRadius.small,
  overflow: "hidden",
});

const PreviewHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  padding: `${padding.small1} ${padding.medium1}`,
  background: colors.blue19,
  borderBottom: `1px solid ${colors.blue01}`,
});

const PreviewTitle = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.blue01,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const PreviewCount = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.normal1,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
});

const PreviewTableWrap = styled(Box)({
  overflow: "auto",
  maxHeight: "35vh",
});

const PreviewTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: fontSize.xsmall,
});

const PreviewTh = styled("th")({
  textAlign: "left",
  padding: `${padding.xsmall2} ${padding.small1}`,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  background: colors.white,
  borderBottom: `1px solid ${colors.grey09}`,
});

const PreviewThChanged = styled(PreviewTh)({
  color: colors.blue15,
});

const PreviewTd = styled("td")({
  padding: `${padding.xsmall2} ${padding.small1}`,
  color: colors.black,
  whiteSpace: "nowrap",
  borderTop: `1px solid ${colors.grey09}`,
});

const OldValue = styled("span")({
  textDecoration: "line-through",
  color: colors.grey11,
  marginRight: gaps.xsmall2,
});

const NewValue = styled("span")({
  color: "#00875A",
  fontWeight: fontWeight.bold1,
});

// Maps edit-field keys to the corresponding record property for showing old values.
const FIELD_KEY_TO_RECORD_PROP: Record<string, string> = {
  state: "StateCode",
  countyCode: "CountyDisplayName",
  policyType: "ProductTypeAbbrTypeName",
  policyFormName: "PolicyFormName",
  agencyLocation: "LocationDisplayName",
  municipality: "PropertyCity",
  policyDate: "PolicyDate",
  issueDate: "IssueDate",
  receivedDate: "ReceivedDate",
  voidedDate: "VoidedDate",
  originalPolicy: "OriginalPolicyNumber",
  originalPolicyDate: "OriginalPolicyDate",
  transCode: "TransCode",
  recordType: "RecordTypeName",
  propertyType: "PropertyTypeName",
  reportedLiability: "LiabilityAmount",
  reportedPremium: "ActualFee",
  reportedRetention: "ActualRemittance",
  reportedTax: "ActualPremiumTax",
  liability: "LiabilityAmount",
  premium: "ActualFee",
  retention: "CalculatedRemittance",
  tax: "CalculatedPremiumTax",
  origPolicyAmt: "OriginalPolicyDate",
  reissueLiab: "ReissueLiabilityAmount",
  siLiab: "SILiabilityAmount",
  addlLiab: "AdditionalLiabilityAmount",
};

const getOldValue = (
  record: PolicyManagementRecord,
  fieldKey: string
): string => {
  const prop = FIELD_KEY_TO_RECORD_PROP[fieldKey];
  if (!prop) return "—";
  const val = (record as unknown as Record<string, unknown>)[prop];
  if (val == null || val === "") return "—";
  return String(val);
};

// `value` holds the submit value (for Agency Location this is the
// LocationUniqueID). `valueLabel` holds a human-friendly label shown in the
// preview (for Agency Location this is the LocationDisplayName).
type Row = { rowId: string; key: string; value: string; valueLabel?: string, companyId?: string, companyName?: string, companyLegacyID?: string, locationAgencyCompanyID?: string, locationReportingID?: string };

type SplitRow = {
  id: string;
  policy: string;
  period: string;
  booked: string;
  premium: number;
  retention: number;
  origNewSplit: number;
  OriginalRemitSplitPct: number | null;
  newSplit: string;
};

const fmtCurrency = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const fmtPct = (n: number) => `${n.toFixed(2)}%`;

const toNum = (s: string) => {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const sanitizeSplitPct = (raw: string) => {
  const value = raw.replace(/[^\d.]/g, "").match(/^\d{0,3}(\.\d{0,2})?/)?.[0] ?? "";
  return Number(value) > 100 ? "100" : value;
};

const buildSplitRows = (records: PolicyManagementRecord[]): SplitRow[] =>
  records.map((r, i) => {
    // Premium comes from ActualFee, Retention from ActualRemittance.
    const premium = Number(r.ActualFee) || 0;
    const retention = Number(r.ActualRemittance) || 0;
    const origNewSplit = Number(r.NewRemitSplitPct) || 0;
    return {
      id: r.uniqueIdentifier || `split-${i}`,
      policy: r.PolicyNumber || "---",
      period: r.AccountingPeriod || "---",
      booked: r.bookedRow
        ? r.bookedRow.charAt(0).toUpperCase() + r.bookedRow.slice(1)
        : "—",
      premium,
      retention,
      origNewSplit,
      OriginalRemitSplitPct: r.OriginalRemitSplitPct ?? null,
      // New Split % starts blank; the user must enter/calculate a value.
      newSplit: "",
    };
  });

interface EditFieldsModalProps {
  open: boolean;
  onClose: () => void;
  records: PolicyManagementRecord[];
  showPcWarning?: boolean;
  onApply?: (
    changes: Array<{ key: string; value: string }>,
    note: string,
    newRemitSplitPcts?: Array<{ PolicyTransactionID: number; NewRemitSplitPct: number }>
  ) => void;
}

const MAX_NOTE_CHARS = 1000;

const EditFieldsModal = ({
  open,
  onClose,
  records,
  showPcWarning = false,
  onApply,
}: EditFieldsModalProps) => {
  // Stable row-id counter; uses a ref-like in state so it survives renders.
  const [rowCounter, setRowCounter] = useState(1);
  const [rows, setRows] = useState<Row[]>([
    { rowId: "r1", key: "", value: "" },
  ]);
  const [note, setNote] = useState("");

  // Form context required by AgencyLocationField (react-hook-form Controller).
  const methods = useForm({ mode: "onBlur", reValidateMode: "onBlur" });

  const propertyTypeOptions = usePropertyTypeOptions();
  const recordTypeOptions = useRecordTypeOptions();
  const policyTypeOptions = usePolicyTypeOptions();
  const { stateList } = useStateList();
  // useStateList returns richer StateOption rows; keep only label/value.
  const stateOptions = useMemo<OptionRenderType[]>(
    () => stateList.map((s) => ({ label: s.label, value: s.value })),
    [stateList]
  );

  const getSelectOptions = (field: EditField): OptionRenderType[] => {
    if (field.optionsSource === "propertyType") return propertyTypeOptions;
    if (field.optionsSource === "recordType") return recordTypeOptions;
    if (field.optionsSource === "policyType") return policyTypeOptions;
    if (field.optionsSource === "state") return stateOptions;
    return (field.options || []).map((opt) => ({ label: opt, value: opt }));
  };

  // Display label for a stored row value. Select values are stored as the
  // submit value (e.g. PropertyTypeCode); show the matching option label.
  const getDisplayValue = (fieldKey: string, value: string): string => {
    const field = EDIT_FIELD_BY_KEY[fieldKey];
    if (field?.type === "select") {
      const match = getSelectOptions(field).find((o) => o.value === value);
      return match?.label ?? value;
    }
    return value;
  };

  // Remit Split state — expanded by default to match the mockup.
  const [splitsOpen, setSplitsOpen] = useState(true);
  const [bulkSplit, setBulkSplit] = useState("");
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);

  // Reset state every time the modal opens fresh.
  useEffect(() => {
    if (open) {
      setRowCounter(1);
      setRows([{ rowId: "r1", key: "", value: "" }]);
      setNote("");
      methods.reset();
      setSplitsOpen(true);
      setBulkSplit("");
      setSplitRows(buildSplitRows(records));
    }
  }, [open, records]);

  // Calculate All: fill every row's New Split % with the toolbar value.
  const calcAllSplits = () => {
    setSplitRows((prev) =>
      prev.map((r) => ({ ...r, newSplit: bulkSplit }))
    );
  };

  const setRowSplit = (id: string, value: string) => {
    setSplitRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, newSplit: value } : r))
    );
  };

  // Derived new Premium/Retention per row. New Retention = Premium × split%;
  // New Premium = Premium × (1 - split%). Em dash when split not yet entered.
  const splitComputed = useMemo(
    () =>
      splitRows.map((r) => {
        const hasSplit = r.newSplit.trim() !== "";
        const split = toNum(r.newSplit) / 100;
        // "changed" = user entered a value that differs from the record's
        // current New Split %. Drives the preview strike-through and Apply.
        const changed = hasSplit && toNum(r.newSplit) !== r.origNewSplit;
        return {
          ...r,
          hasSplit,
          changed,
          newPremium: hasSplit ? r.premium * (1 - split) : null,
          newRetention: hasSplit ? r.premium * split : null,
        };
      }),
    [splitRows]
  );

  // Lookup of computed split rows by record id, so the Preview table can show
  // each record's Orig Split % → New Split % alongside the other field changes.
  const splitByRecordId = useMemo(
    () => new Map(splitComputed.map((r) => [r.id, r])),
    [splitComputed]
  );

  const usedKeys = new Set(rows.map((r) => r.key).filter(Boolean));

  const addRow = () => {
    const next = rowCounter + 1;
    setRowCounter(next);
    setRows((prev) => [...prev, { rowId: `r${next}`, key: "", value: "" }]);
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => {
      if (prev.length === 1) {
        // Never go to zero rows — reset the last one to blank.
        return [{ rowId: prev[0].rowId, key: "", value: "" }];
      }
      return prev.filter((r) => r.rowId !== rowId);
    });
  };

  const setField = (rowId: string, key: string) => {
    // Clear any stale Agency Location text held in the form when the field
    // for this row changes.
    methods.setValue(`agencyLocation-${rowId}`, "");
    setRows((prev) =>
      prev.map((r) =>
        r.rowId === rowId ? { ...r, key, value: "", valueLabel: undefined } : r
      )
    );
  };

  const setValue = (rowId: string, value: string) => {
    setRows((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, value } : r))
    );
  };

  // Agency Location: store LocationUniqueID as the submit value and
  // LocationDisplayName as the preview label.
  const setLocation = (rowId: string, location: CompanyLocation | string) => {
    if (!location || typeof location === "string") return;
    setRows((prev) =>
      prev.map((r) =>
        r.rowId === rowId
          ? {
              ...r,
              value: location.locationUniqueID ?? "",
              valueLabel: location.locationDisplayName ?? "",
              companyId: location.companyID ?? "",
              companyName: location.companyName ?? "",
              companyLegacyID: location.companyLegacyID ?? "",
              locationAgencyCompanyID: location.locationAgencyCompanyID ?? "",
              locationReportingID: location.locationReportingID ?? "",
            }
          : r
      )
    );
  };

  const clearLocation = (rowId: string) => {
    methods.setValue(`agencyLocation-${rowId}`, "");
    setRows((prev) =>
      prev.map((r) =>
        r.rowId === rowId ? { ...r, value: "", valueLabel: undefined } : r
      )
    );
  };

  const showSplitPercentage = rows.some((r) => r.key === "splitPercentage");
  const hasSplitEdits =
    showSplitPercentage && splitComputed.some((r) => r.changed);

  // Split Percentage rows have no direct value control — their "filled"
  // state comes from the Remit Split table instead.
  const rowIsFilled = (r: Row) =>
    r.key === "splitPercentage" ? hasSplitEdits : r.value !== "";

  const hasValidChange = rows.some((r) => r.key !== "" && rowIsFilled(r));
  const hasNote = note.trim().length > 0;

  const canApply = hasValidChange || hasNote || hasSplitEdits;

  const selectedRecordsCount = records.length;
  const fieldsCount = rows.filter((r) => r.key && rowIsFilled(r)).length;

  const handleApply = () => {
    if (!canApply) return;
    const changes = rows.filter((r) => r.key && r.value !== "");
    // Agency Location maps to two API fields: LocationUniqueID (the selected
    // value) and LocationDisplayName (the label). All other fields map 1:1.

    const flattened = changes.flatMap((r) =>
      r.key === "agencyLocation"
        ? [
            { key: "CompanyID", value: r.companyId ?? "" },
            { key: "CompanyName", value: r.companyName ?? "" },
            { key: "CompanyLegacyID", value: r.companyLegacyID ?? "" },
            { key: "LocationUniqueID", value: r.value },
            { key: "LocationDisplayName", value: r.valueLabel ?? "" },
            { key: "LocationReportingID", value: r.locationReportingID ?? "" },
            { key: "LocationAgencyCompanyID", value: r.locationAgencyCompanyID ?? "" },
          ]
        : [{ key: r.key, value: r.value }]
    );

    // New Remit Split % rows are per-record; only send rows the user actually
    // changed from the record's current New Split % — and only while the
    // Split Percentage field is selected.
    const newRemitSplitPcts = (showSplitPercentage ? splitComputed : [])
      .filter((r) => r.changed)
      .map((r) => ({
        PolicyTransactionID: Number(r.id),
        NewRemitSplitPct: Number(r.newSplit),
        ActualFee: r.newPremium,
        ActualRetention: r.newRetention,
      }));

    onApply?.(flattened, note.trim(), newRemitSplitPcts);
    onClose();
  };

  // Build the grouped <Select> children for the Field dropdown.
  // We render each group's items inline; the disabled-on-used logic is per row.
  const renderFieldOptions = (currentKey: string) => {
    const children: React.ReactNode[] = [
      <MenuItem key="__none" value="">
        - Select field -
      </MenuItem>,
    ];
    EDIT_FIELD_GROUPS.forEach((group) => {
      children.push(
        <ListSubheader
          key={`group-${group}`}
          sx={{
            fontWeight: fontWeight.bold2,
            color: colors.blue15,
            fontSize: fontSize.xsmall,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            lineHeight: 2,
          }}
        >
          {group}
        </ListSubheader>
      );
      EDIT_FIELDS_BY_GROUP[group].forEach((f) => {
        const isDisabled = usedKeys.has(f.key) && currentKey !== f.key;
        children.push(
          <MenuItem
            key={f.key}
            value={f.key}
            disabled={isDisabled}
            sx={{ paddingLeft: padding.large1 }}
          >
            {f.label}
          </MenuItem>
        );
      });
    });
    return children;
  };

  const renderValueControl = (row: Row) => {
    if (!row.key) {
      return (
        <TextField
          fullWidth
          size="small"
          disabled
          value=""
          placeholder="Select a field first"
        />
      );
    }
    const field = EDIT_FIELD_BY_KEY[row.key];
    if (!field) return null;

    if (row.key === "splitPercentage") {
      return (
        <SplitNotice>
          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
          <span>
            Set per-record values in the <strong>Remit Split</strong>{" "}
            section above.
          </span>
        </SplitNotice>
      );
    }

    switch (field.type) {
      case "agencyLocation":
        return (
          <AgencyLocationField
            label=""
            name={`agencyLocation-${row.rowId}`}
            size="small"
            onChange={(location) => setLocation(row.rowId, location)}
            onClear={() => clearLocation(row.rowId)}
          />
        );
      case "select":
        return (
          <Select
            fullWidth
            size="small"
            value={row.value}
            onChange={(e) => setValue(row.rowId, e.target.value as string)}
            displayEmpty
          >
            <MenuItem value="">- Select -</MenuItem>
            {getSelectOptions(field).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        );
      case "date":
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={row.value}
            onChange={(e) => setValue(row.rowId, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );
      case "dollar":
        return (
          <TextField
            fullWidth
            size="small"
            type="text"
            value={row.value}
            placeholder="0.00"
            onChange={(e) => setValue(row.rowId, e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        );
      case "text":
      default:
        return (
          <TextField
            fullWidth
            size="small"
            type="text"
            value={row.value}
            placeholder={field.placeholder || ""}
            onChange={(e) => setValue(row.rowId, e.target.value)}
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogHeading>
        <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1 }}>
          <EditNoteIcon sx={{ color: colors.blue01 }} />
          <span>Bulk Edit</span>
        </Box>
      </DialogHeading>
      <Subtitle>
        Set field values on {selectedRecordsCount} selected record
        {selectedRecordsCount === 1 ? "" : "s"}
      </Subtitle>
      <DialogContent>
        <FormProvider {...methods}>
        <Box>
          {/* PCQ warning banner — visible when opened from Pre-Close */}
          {showPcWarning && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: gaps.small1,
                marginBottom: gaps.medium1,
                padding: `${padding.small1} ${padding.medium1}`,
                backgroundColor: "#fff8e5",
                border: "1px solid #e8c068",
                borderRadius: borderRadius.small,
                color: "#7a5a00",
              }}
            >
              <WarningAmberIcon
                sx={{ color: "#c49a24", fontSize: 18, flexShrink: 0 }}
              />
              <Box component="span" sx={{ fontSize: fontSize.small }}>
                <strong>
                  Editing transaction or financial fields returns records to
                  Quality Review.
                </strong>{" "}
                Pre-Close records have already passed TVE; any change to a
                validated field requires re-validation.
              </Box>
            </Box>
          )}

          {/* Remit Split — only shown when Split Percentage is a selected field */}
          {showSplitPercentage && (
          <SplitsSection>
            <SplitsHeader onClick={() => setSplitsOpen((o) => !o)}>
              <ExpandMoreIcon
                sx={{
                  color: colors.grey11,
                  transform: splitsOpen ? "none" : "rotate(-90deg)",
                  transition: "transform 150ms",
                }}
              />
              <SplitsTitle>Remit Split</SplitsTitle>
            </SplitsHeader>
            <Collapse in={splitsOpen} unmountOnExit>
              <SplitsBody>
                <CalcToolbar>
                  <CalcLabel>New Split:</CalcLabel>
                  <SplitPctInput
                    size="small"
                    placeholder="0.00"
                    value={bulkSplit}
                    onChange={(e) =>
                      setBulkSplit(sanitizeSplitPct(e.target.value))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<CalculateIcon />}
                    onClick={calcAllSplits}
                  >
                    Calculate All
                  </ActionButton>
                  <CalcHint>
                    Fills <strong>New Split %</strong> for all rows, then
                    computes <strong>New Retention</strong> as Premium × New
                    Split %. Premium unchanged.
                  </CalcHint>
                </CalcToolbar>

                <SplitsTableWrap>
                  <SplitsTable>
                    <thead>
                      <tr>
                        <Th>Policy</Th>
                        <Th>Period</Th>
                        <Th>Booked</Th>
                        <Th style={{ textAlign: "right" }}>Premium</Th>
                        <Th style={{ textAlign: "right" }}>Retention</Th>
                        <Th style={{ textAlign: "right" }}>Orig Split %</Th>
                        <ThNew>New Split %</ThNew>
                        <ThNew style={{ textAlign: "right" }}>New Premium</ThNew>
                        <ThNew style={{ textAlign: "right" }}>
                          New Retention
                        </ThNew>
                      </tr>
                    </thead>
                    <tbody>
                      {splitComputed.map((r) => (
                        <tr key={r.id}>
                          <Td>{r.policy}</Td>
                          <Td>{r.period}</Td>
                          <Td>{r.booked}</Td>
                          <Td style={{ textAlign: "right" }}>
                            {fmtCurrency(r.premium)}
                          </Td>
                          <Td style={{ textAlign: "right" }}>
                            {fmtCurrency(r.retention)}
                          </Td>
                          <Td style={{ textAlign: "right" }}>
                            {r.OriginalRemitSplitPct != null
                              ? fmtPct(r.OriginalRemitSplitPct)
                              : "—"}
                          </Td>
                          <TdNew>
                            <SplitPctInput
                              size="small"
                              placeholder="0.00"
                              value={r.newSplit}
                              onChange={(e) =>
                                setRowSplit(r.id, sanitizeSplitPct(e.target.value))
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    %
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </TdNew>
                          <TdNew style={{ textAlign: "right" }}>
                            {r.newPremium === null
                              ? "—"
                              : fmtCurrency(r.newPremium)}
                          </TdNew>
                          <TdNew style={{ textAlign: "right" }}>
                            {r.newRetention === null
                              ? "—"
                              : fmtCurrency(r.newRetention)}
                          </TdNew>
                        </tr>
                      ))}
                    </tbody>
                  </SplitsTable>
                  {splitComputed.length === 0 && (
                    <EmptySplits>No records to split.</EmptySplits>
                  )}
                </SplitsTableWrap>
              </SplitsBody>
            </Collapse>
          </SplitsSection>
          )}

          {/* Field Edits */}
          <HeaderLabel sx={{ marginBottom: gaps.xsmall2 }}>
            Field Edits
          </HeaderLabel>

          {/* Header strip */}
          <RowsGrid sx={{ marginBottom: gaps.xsmall2 }}>
            <HeaderLabel>Field</HeaderLabel>
            <HeaderLabel>New Value</HeaderLabel>
            <Box />
          </RowsGrid>

          {/* Dynamic rows */}
          {rows.map((row) => (
            <RowsGrid
              key={row.rowId}
              sx={{ marginBottom: gaps.xsmall2 }}
            >
              <Select
                fullWidth
                size="small"
                value={row.key}
                onChange={(e) => setField(row.rowId, e.target.value as string)}
                displayEmpty
              >
                {renderFieldOptions(row.key)}
              </Select>
              <Box>{renderValueControl(row)}</Box>
              <IconButton
                size="small"
                onClick={() => removeRow(row.rowId)}
                aria-label="Remove row"
                sx={{
                  border: `1px solid ${colors.grey09}`,
                  borderRadius: borderRadius.small,
                  width: 36,
                  height: 36,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </RowsGrid>
          ))}

          <AddRowButton
            type="button"
            onClick={addRow}
            disabled={rows.some((r) => !rowIsFilled(r))}
          >
            <AddIcon fontSize="small" />
            Add another field
          </AddRowButton>

          {/* Preview table — shows old→new per record when fields have values */}
          {fieldsCount > 0 && (
            <PreviewSection sx={{ marginTop: gaps.large1 }}>
              <PreviewHeader>
                <PreviewTitle>Preview</PreviewTitle>
                <PreviewCount>
                  – {selectedRecordsCount} record{selectedRecordsCount === 1 ? "" : "(s)"} ·{" "}
                  {fieldsCount} field{fieldsCount === 1 ? "" : "(s)"} will change
                </PreviewCount>
              </PreviewHeader>
              <PreviewTableWrap>
                <PreviewTable>
                  <thead>
                    <tr>
                      <PreviewTh>Policy Number</PreviewTh>
                      <PreviewTh>Agent</PreviewTh>
                      <PreviewTh>State</PreviewTh>
                      {rows
                        .filter((r) => r.key && r.value !== "")
                        .map((r) => (
                          <PreviewThChanged key={r.rowId}>
                            {EDIT_FIELD_BY_KEY[r.key]?.label ?? r.key} →
                          </PreviewThChanged>
                        ))}
                      {hasSplitEdits && (
                        <PreviewThChanged>New Split % →</PreviewThChanged>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((rec) => (
                      <tr key={rec.uniqueIdentifier || rec.PolicyNumber}>
                        <PreviewTd
                          style={{ color: colors.blue01, fontWeight: fontWeight.bold1 }}
                        >
                          {rec.PolicyNumber || "—"}
                        </PreviewTd>
                        <PreviewTd>{rec.AgentID || "—"}</PreviewTd>
                        <PreviewTd>{rec.StateCode || "—"}</PreviewTd>
                        {rows
                          .filter((r) => r.key && r.value !== "")
                          .map((r) => {
                            const oldVal = getOldValue(rec, r.key);
                            return (
                              <PreviewTd key={r.rowId}>
                                <OldValue>{oldVal}</OldValue>
                                <NewValue>
                                  {r.valueLabel ??
                                    getDisplayValue(r.key, r.value)}
                                </NewValue>
                              </PreviewTd>
                            );
                          })}
                        {hasSplitEdits &&
                          (() => {
                            const sr = splitByRecordId.get(
                              rec.uniqueIdentifier ?? ""
                            );
                            return (
                              <PreviewTd>
                                {sr && sr.changed ? (
                                  <>
                                    <OldValue>
                                      {fmtPct(sr.origNewSplit)}
                                    </OldValue>
                                    <NewValue>
                                      {fmtPct(toNum(sr.newSplit))}
                                    </NewValue>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </PreviewTd>
                            );
                          })()}
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
              </PreviewTableWrap>
            </PreviewSection>
          )}

          {/* Note section */}
          <NoteSection>
            <NoteHeader>
              <Typography
                sx={{
                  fontSize: fontSize.xxsmall,
                  fontWeight: fontWeight.bold2,
                  color: colors.grey11,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                Note{" "}
                <span
                  style={{
                    fontWeight: 400,
                    textTransform: "none",
                    letterSpacing: 0,
                    color: colors.grey11,
                  }}
                >
                  (optional - applies to every selected record)
                </span>
              </Typography>
              <Typography
                sx={{ fontSize: fontSize.xxsmall, color: colors.grey11 }}
              >
                {note.length} / {MAX_NOTE_CHARS} characters
              </Typography>
            </NoteHeader>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              placeholder="Write a note that will be added to every selected record"
              value={note}
              onChange={(e) =>
                setNote(e.target.value.slice(0, MAX_NOTE_CHARS))
              }
              inputProps={{ maxLength: MAX_NOTE_CHARS }}
            />
          </NoteSection>
        </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Typography
          sx={{
            fontSize: fontSize.xsmall,
            color: colors.grey11,
            marginRight: "auto",
          }}
        >
          {selectedRecordsCount} record
          {selectedRecordsCount === 1 ? "" : "s"} · {fieldsCount} field
          {fieldsCount === 1 ? "" : "s"}
        </Typography>
        <ActionButton variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          disabled={!canApply}
          onClick={handleApply}
        >
          Apply {fieldsCount} Change{fieldsCount === 1 ? "" : "s"} to{" "}
          {selectedRecordsCount} Record{selectedRecordsCount === 1 ? "" : "s"}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditFieldsModal;
